import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "../lib/config";
import { loadGoogleMaps } from "../lib/googleMaps";
import { loadMarkerClusterer } from "../lib/markerClusterer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import CrewWorkflowButtons from "./CrewWorkflowButtons";
import JobDetailsList from "./JobDetailsList";
import {
  Loader2, MapPin, Navigation, Crosshair, Settings as SettingsIcon,
  ChevronLeft, ChevronRight, RefreshCw, Calendar as CalendarIcon, Route,
  DollarSign, Clock, MapPinned, Sparkles, Phone, MessageSquare, CheckCircle2,
  ExternalLink, CalendarClock, X,
} from "lucide-react";
import { toast } from "sonner";
import JobActionsMenu, { StatusBadge, PaymentBadge } from "./JobActionsMenu";

/* ============== helpers ============== */
function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function shiftDate(iso, deltaDays) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}
function fmtMinutes(secs) {
  if (!secs && secs !== 0) return "—";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
function fmtMiles(meters) {
  if (!meters && meters !== 0) return "—";
  return `${(meters / 1609.344).toFixed(1)} mi`;
}

// Status → marker fill color
// Green = Paid, Yellow = Scheduled, Blue = Completed, Red = Overdue/Cancelled
function jobMarkerColor(job) {
  const status = (job.status || "").toLowerCase();
  const pay = (job.payment_status || "").toLowerCase();
  if (status === "completed") return "#1D4ED8";          // blue
  if (status === "cancelled" || status === "canceled") return "#DC2626"; // red
  if (pay === "paid" || pay === "paid_in_full") return "#059669"; // green = paid
  if (status === "in_progress" || status === "on_the_way" || status === "arrived")
    return "#F59E0B"; // amber = active
  // Overdue = scheduled in the past
  if (status === "scheduled" || status === "confirmed" || status === "pending"
      || status === "pending_payment_verification") {
    const d = job.preferred_date;
    if (d && d < todayIso()) return "#DC2626"; // overdue
    return "#EAB308"; // yellow = scheduled
  }
  return "#6B7280";
}

// Build an SVG pin with a stop number
function pinSvg(color, number) {
  const text = number != null ? String(number) : "";
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
      <defs>
        <filter id="s" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
        </filter>
      </defs>
      <path filter="url(#s)" d="M20 0C9 0 0 8.7 0 19.5 0 33 18 50 19.3 51.3a1 1 0 0 0 1.4 0C22 50 40 33 40 19.5 40 8.7 31 0 20 0z" fill="${color}"/>
      <circle cx="20" cy="19" r="11" fill="white"/>
      <text x="20" y="23.5" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="13" font-weight="800" fill="${color}">${text}</text>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function startSvg() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
      <circle cx="22" cy="22" r="18" fill="#0F172A" stroke="#10B981" stroke-width="3"/>
      <path d="M14 22l5 5 11-11" stroke="#10B981" stroke-width="3.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Dashed amber ghost pin to highlight unscheduled nearby customers
function opportunitySvg() {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">
      <defs>
        <filter id="og" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
        </filter>
      </defs>
      <path filter="url(#og)" d="M18 0C8 0 0 7.7 0 17.5 0 30 16.2 44 17.4 45.2a1 1 0 0 0 1.2 0C19.8 44 36 30 36 17.5 36 7.7 28 0 18 0z" fill="#FFFFFF" stroke="#D97706" stroke-width="2.5" stroke-dasharray="4 3"/>
      <circle cx="18" cy="17" r="9" fill="#FBBF24"/>
      <text x="18" y="21" text-anchor="middle" font-family="Inter, system-ui, sans-serif" font-size="13" font-weight="900" fill="#7C2D12">+</text>
    </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

/* ============== component ============== */
export default function RouteMapPanel() {
  const [date, setDate] = useState(todayIso());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);          // { date, jobs, start, stats }
  const [settings, setSettings] = useState(null);  // admin settings
  const [routeStats, setRouteStats] = useState({ meters: 0, seconds: 0 });
  const [orderedJobs, setOrderedJobs] = useState([]);   // jobs in optimized order
  const [unmappedCount, setUnmappedCount] = useState(0);
  const [selectedJob, setSelectedJob] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [opportunities, setOpportunities] = useState([]);   // ghost pins for nearby unscheduled customers
  const [opportunityImpact, setOpportunityImpact] = useState(null);
  // iter170 — Opportunities disabled. The business owner wants ONLY the
  // day's active customer pins on the Route Map — no yellow "+" ghost
  // pins, no cluster markers, no upsell suggestions. Simplifies the
  // dispatch view to a pure "who's left to visit" board.
  const [showOpportunities] = useState(false);
  const [oppRadius, setOppRadius] = useState(5);
  const [oppBusy, setOppBusy] = useState({});         // user_id -> bool
  // iter168 — Client-side payment-status filter. "all" is the default so
  // every scheduled job is visible on the map (previously the map hid
  // unpaid/pending jobs, which broke dispatch for pay-after-service).
  const [paymentFilter, setPaymentFilter] = useState("all");
  // iter169 — Hide completed jobs by default so the crew sees only what
  // still needs work today. Toggle to un-hide.
  const [showCompleted, setShowCompleted] = useState(false);

  const mapRef = useRef(null);
  const mapElRef = useRef(null);
  const gmapsRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const polylineRef = useRef(null);
  const markersRef = useRef([]);
  const opportunityMarkersRef = useRef([]);
  const startMarkerRef = useRef(null);
  const clustererRef = useRef(null);
  const infoWindowRef = useRef(null);

  /* -------- data fetch -------- */
  const loadDay = useCallback(async (d) => {
    setLoading(true);
    try {
      const [routesRes, settingsRes] = await Promise.all([
        api.get("/admin/routes", { params: { date: d } }),
        api.get("/admin/settings"),
      ]);
      setData(routesRes.data);
      setSettings(settingsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load routes");
    } finally { setLoading(false); }
  }, []);

  /* -------- nearby opportunities fetch -------- */
  const loadOpportunities = useCallback(async (_d, _r) => {
    // iter170 — Opportunities disabled per business-owner request. No
    // "+" pins, no upsell banner. Skip the fetch entirely to save one
    // request per Route page load.
    setOpportunities([]);
    setOpportunityImpact(null);
  }, []);

  useEffect(() => { loadDay(date); }, [date, loadDay]);
  useEffect(() => { loadOpportunities(date, oppRadius); }, [date, oppRadius, loadOpportunities]);

  /* -------- mount the Google Map exactly once -------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const google = await loadGoogleMaps();
        if (cancelled || !mapElRef.current || mapRef.current) return;
        gmapsRef.current = google;
        const map = new google.maps.Map(mapElRef.current, {
          center: { lat: 30.3322, lng: -81.6557 }, // Jacksonville, FL
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          clickableIcons: false,
          gestureHandling: "greedy",
          styles: [
            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
            { featureType: "transit", elementType: "labels", stylers: [{ visibility: "off" }] },
          ],
        });
        mapRef.current = map;
        directionsRendererRef.current = new google.maps.DirectionsRenderer({
          map,
          suppressMarkers: true,
          preserveViewport: false,
          polylineOptions: { strokeColor: "#047857", strokeWeight: 5, strokeOpacity: 0.85 },
        });
        infoWindowRef.current = new google.maps.InfoWindow();
      } catch (e) {
        console.error("Maps init failed", e);
        toast.error("Could not load Google Maps");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* -------- compute / draw route whenever data or start changes -------- */
  const computeRoute = useCallback(async () => {
    const google = gmapsRef.current;
    const map = mapRef.current;
    if (!google || !map || !data) return;
    // iter170 — Filter OUT completed and cancelled jobs from the map
    // entirely (unless the admin explicitly toggles "Show completed").
    // The map should behave like a live dispatch board — only pins for
    // stops the crew still needs to visit today. Renumbering happens
    // automatically because stop numbers are derived from the position
    // in this filtered array.
    const activeStatuses = showCompleted
      ? null
      : new Set(["scheduled", "confirmed", "on_the_way", "arrived", "in_progress"]);
    const isVisible = (j) => {
      const s = (j.status || "scheduled").toLowerCase();
      if (activeStatuses === null) return true;
      return activeStatuses.has(s);
    };
    const jobs = (data.jobs || []).filter((j) => isVisible(j) && j.lat && j.lng);
    const unmapped = (data.jobs || []).filter((j) => isVisible(j) && !(j.lat && j.lng) && j.address);

    // Geocode jobs with addresses but no coords (one-off, then cache server-side)
    if (unmapped.length) {
      const geocoder = new google.maps.Geocoder();
      for (const j of unmapped) {
        try {
          const { results } = await geocoder.geocode({ address: j.address });
          const r = results && results[0];
          if (r) {
            const loc = r.geometry.location;
            j.lat = loc.lat();
            j.lng = loc.lng();
            jobs.push(j);
            // cache server-side
            api.post(`/admin/bookings/${j.booking_id}/geo`, {
              lat: j.lat, lng: j.lng, formatted_address: r.formatted_address,
            }).catch(() => {});
          }
        } catch (_e) { /* skip */ }
      }
    }

    // Wipe previous markers + cluster + route
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (clustererRef.current) { clustererRef.current.clearMarkers(); clustererRef.current = null; }
    if (startMarkerRef.current) { startMarkerRef.current.setMap(null); startMarkerRef.current = null; }
    if (polylineRef.current) { polylineRef.current.setMap(null); polylineRef.current = null; }
    if (directionsRendererRef.current) directionsRendererRef.current.setDirections({ routes: [] });
    setRouteStats({ meters: 0, seconds: 0 });
    // unmapped count is fully derived from the final job set
    setUnmappedCount(Math.max(0, (data.jobs || []).length - jobs.length));
    setOrderedJobs(jobs);

    // iter170 — Empty state: nothing left to visit today. Center on
    // Jacksonville and let the parent render the "Today's route is
    // complete" placeholder.
    if (!jobs.length) {
      map.setCenter({ lat: 30.3322, lng: -81.6557 });
      map.setZoom(11);
      return;
    }
    // iter170 — Single stop → center + zoom in tightly so the crew has
    // a clear "you have one stop left" view.
    if (jobs.length === 1) {
      const only = jobs[0];
      map.setCenter({ lat: only.lat, lng: only.lng });
      map.setZoom(15);
    }

    // Resolve start point
    const start = data.start || {};
    let origin = null;
    if (start.lat && start.lng) origin = { lat: start.lat, lng: start.lng };
    else if (start.address) origin = start.address;

    // If only one job + no origin → just drop the pin, fit
    if (!origin || jobs.length === 0) {
      // place plain numbered markers (no route)
      placeMarkers(jobs.map((j, i) => ({ ...j, _stop: i + 1 })));
      fitToMarkers(jobs);
      return;
    }

    // Optimize with Google Routes API (new). Falls back to legacy
    // DirectionsService if the new API isn't enabled for the key.
    setOptimizing(true);
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const originPt = typeof origin === "object"
        ? { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } }
        : { address: origin };
      const last = jobs[jobs.length - 1];
      const intermediates = jobs.slice(0, -1).map((j) => ({
        location: { latLng: { latitude: j.lat, longitude: j.lng } },
      }));
      const destination = {
        location: { latLng: { latitude: last.lat, longitude: last.lng } },
      };

      let optimizedOrder = null;
      let polylineEncoded = null;
      let totalMeters = 0;
      let totalSeconds = 0;

      // -------- TRY 1: new Routes API ----------
      try {
        const r = await fetch(
          "https://routes.googleapis.com/directions/v2:computeRoutes",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Goog-Api-Key": apiKey,
              "X-Goog-FieldMask":
                "routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline,routes.optimizedIntermediateWaypointIndex",
            },
            body: JSON.stringify({
              origin: originPt,
              destination,
              intermediates,
              travelMode: "DRIVE",
              routingPreference: "TRAFFIC_AWARE",
              optimizeWaypointOrder: true,
            }),
          }
        );
        if (!r.ok) throw new Error(`Routes API ${r.status}`);
        const j = await r.json();
        const route = j.routes && j.routes[0];
        if (!route) throw new Error("Routes API: empty response");
        optimizedOrder = route.optimizedIntermediateWaypointIndex || [];
        polylineEncoded = route.polyline?.encodedPolyline || null;
        totalMeters = route.distanceMeters || 0;
        // duration is a string like "1234s"
        totalSeconds = parseInt(String(route.duration || "0s").replace(/\D/g, ""), 10);
      } catch (newApiErr) {
        // -------- TRY 2: legacy DirectionsService ----------
        try {
          const ds = new google.maps.DirectionsService();
          const res = await ds.route({
            origin,
            destination: new google.maps.LatLng(last.lat, last.lng),
            waypoints: jobs.slice(0, -1).map((j) => ({
              location: new google.maps.LatLng(j.lat, j.lng),
              stopover: true,
            })),
            optimizeWaypoints: true,
            travelMode: google.maps.TravelMode.DRIVING,
          });
          // Render via legacy DirectionsRenderer
          directionsRendererRef.current.setDirections(res);
          const route = res.routes[0];
          optimizedOrder = route.waypoint_order || [];
          route.legs.forEach((l) => {
            totalMeters += l.distance.value;
            totalSeconds += l.duration.value;
          });
        } catch (legacyErr) {
          throw new Error(
            `Routes API: ${newApiErr.message || newApiErr}; Directions API: ${legacyErr.message || legacyErr}`
          );
        }
      }

      // Reorder jobs according to optimized indices; destination stays last
      const middle = optimizedOrder.map((idx) => jobs[idx]);
      const ordered = [...middle, last].map((j, i) => ({ ...j, _stop: i + 1 }));
      setOrderedJobs(ordered);
      setRouteStats({ meters: totalMeters, seconds: totalSeconds });

      // Render the polyline ourselves if we used the new API
      if (polylineEncoded && google.maps.geometry?.encoding) {
        const path = google.maps.geometry.encoding.decodePath(polylineEncoded);
        if (polylineRef.current) polylineRef.current.setMap(null);
        polylineRef.current = new google.maps.Polyline({
          path,
          map,
          strokeColor: "#047857",
          strokeOpacity: 0.85,
          strokeWeight: 5,
        });
        // fit
        const b = new google.maps.LatLngBounds();
        path.forEach((p) => b.extend(p));
        map.fitBounds(b, 80);
      }

      placeMarkers(ordered, origin);
    } catch (e) {
      console.warn("Route optimization unavailable — falling back to standard route", e);
      toast("Using standard route", { icon: "🧭" });
      placeMarkers(jobs.map((j, i) => ({ ...j, _stop: i + 1 })));
      fitToMarkers(jobs);
    } finally {
      setOptimizing(false);
    }
  }, [data, showCompleted]);

  useEffect(() => { computeRoute(); }, [computeRoute]);

  /* -------- marker rendering + clustering -------- */
  const placeMarkers = useCallback(async (jobs, origin) => {
    const google = gmapsRef.current;
    const map = mapRef.current;
    if (!google || !map) return;

    // start marker
    if (origin && typeof origin === "object" && origin.lat && origin.lng) {
      startMarkerRef.current = new google.maps.Marker({
        position: origin,
        map,
        title: "Start",
        icon: { url: startSvg(), scaledSize: new google.maps.Size(44, 44), anchor: new google.maps.Point(22, 22) },
        zIndex: 9999,
      });
    }

    const ms = jobs.map((j) => {
      const m = new google.maps.Marker({
        position: { lat: j.lat, lng: j.lng },
        title: `${j._stop}. ${j.name || ""}`,
        icon: {
          url: pinSvg(jobMarkerColor(j), j._stop),
          scaledSize: new google.maps.Size(40, 52),
          anchor: new google.maps.Point(20, 50),
        },
      });
      m.addListener("click", () => setSelectedJob(j));
      return m;
    });
    markersRef.current = ms;

    // iter170 — Render every pin individually, always. The MarkerClusterer
    // library was producing "+ N" yellow cluster markers at low zoom
    // which the business owner explicitly does not want (crews were
    // confused by opaque cluster counts vs the numbered stop pins).
    // Instead, every active stop is a distinct numbered pin at ALL
    // zoom levels.
    ms.forEach((m) => m.setMap(map));
  }, []);

  /* -------- opportunity (ghost) marker rendering -------- */
  const drawOpportunityMarkers = useCallback(() => {
    const google = gmapsRef.current;
    const map = mapRef.current;
    if (!google || !map) return;
    // wipe previous opportunity markers
    opportunityMarkersRef.current.forEach((m) => m.setMap(null));
    opportunityMarkersRef.current = [];
    if (!showOpportunities || !opportunities.length) return;
    opportunities.forEach((o) => {
      const m = new google.maps.Marker({
        position: { lat: o.lat, lng: o.lng },
        map,
        title: `Opportunity: ${o.name} · ${o.nearest_job?.distance_miles?.toFixed?.(1)} mi`,
        icon: {
          url: opportunitySvg(),
          scaledSize: new google.maps.Size(36, 46),
          anchor: new google.maps.Point(18, 44),
        },
        zIndex: 5000,
        opacity: 0.92,
      });
      m.addListener("click", () => setSelectedJob({ ...o, _is_opportunity: true, _stop: "+" }));
      opportunityMarkersRef.current.push(m);
    });
  }, [opportunities, showOpportunities]);

  useEffect(() => { drawOpportunityMarkers(); }, [drawOpportunityMarkers, orderedJobs]);

  const fitToMarkers = (jobs) => {
    const google = gmapsRef.current;
    const map = mapRef.current;
    if (!google || !map || !jobs.length) return;
    const b = new google.maps.LatLngBounds();
    jobs.forEach((j) => b.extend({ lat: j.lat, lng: j.lng }));
    map.fitBounds(b, 80);
  };

  /* -------- start location actions -------- */
  const useCurrentGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        try {
          await api.patch("/admin/settings", {
            crew_start_mode: "gps",
            crew_start_lat: lat,
            crew_start_lng: lng,
            crew_start_address: `Current GPS (${lat.toFixed(4)}, ${lng.toFixed(4)})`,
          });
          toast.success("Start location set to your current GPS");
          loadDay(date);
        } catch (e) {
          toast.error("Could not save start location");
        } finally { setGpsBusy(false); }
      },
      (err) => { toast.error(err.message || "GPS denied"); setGpsBusy(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const saveCustomStart = async (address) => {
    if (!address || address.trim().length < 4) {
      toast.error("Enter a valid address"); return;
    }
    try {
      const google = await loadGoogleMaps();
      const { results } = await new google.maps.Geocoder().geocode({ address });
      const r = results && results[0];
      if (!r) { toast.error("Could not geocode that address"); return; }
      const loc = r.geometry.location;
      await api.patch("/admin/settings", {
        crew_start_mode: "custom",
        crew_start_lat: loc.lat(),
        crew_start_lng: loc.lng(),
        crew_start_address: r.formatted_address,
      });
      toast.success("Custom start location saved");
      loadDay(date);
    } catch (e) {
      toast.error("Could not save start location");
    }
  };

  /* -------- derived UI numbers -------- */
  const revenue = useMemo(() => (data?.jobs || []).reduce((s, j) => s + (j.price || 0), 0), [data]);
  const densityScore = useMemo(() => {
    // Higher = more efficient. Heuristic: stops^2 / total_miles, capped 0-100.
    if (!routeStats.meters || !orderedJobs.length) return null;
    const miles = routeStats.meters / 1609.344;
    if (miles <= 0) return null;
    const score = Math.min(100, Math.round((orderedJobs.length * orderedJobs.length * 6) / miles));
    return score;
  }, [orderedJobs.length, routeStats.meters]);

  /* -------- "Open in Maps" links -------- */
  const buildAppleUrl = () => {
    if (!orderedJobs.length) return null;
    const last = orderedJobs[orderedJobs.length - 1];
    return `maps://?daddr=${encodeURIComponent(last.formatted_address || last.address)}`;
  };
  const buildGoogleUrl = () => {
    if (!orderedJobs.length) return null;
    const last = orderedJobs[orderedJobs.length - 1];
    const dest = encodeURIComponent(last.formatted_address || last.address);
    const waypts = orderedJobs.slice(0, -1)
      .map((j) => encodeURIComponent(j.formatted_address || j.address))
      .join("|");
    const origin = (() => {
      const s = data?.start;
      if (s?.lat && s?.lng) return `${s.lat},${s.lng}`;
      if (s?.address) return encodeURIComponent(s.address);
      return "";
    })();
    return `https://www.google.com/maps/dir/?api=1&travelmode=driving${origin ? `&origin=${origin}` : ""}&destination=${dest}${waypts ? `&waypoints=${waypts}` : ""}`;
  };

  const refresh = () => { loadDay(date); loadOpportunities(date, oppRadius); };

  const handleAddOpportunity = async (opp, e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setOppBusy((s) => ({ ...s, [opp.user_id]: true }));
    try {
      await api.post(`/admin/nearby-opportunities/${opp.user_id}/schedule`, {
        date,
        preferred_time_window: opp.suggested_time_window || "morning",
        service_key: opp.service_key,
        square_feet: opp.square_feet,
        yard_area: opp.yard_area,
        frequency: opp.frequency || "one_time",
      });
      toast.success(`${opp.name} added to ${date}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add to route");
    } finally {
      setOppBusy((s) => ({ ...s, [opp.user_id]: false }));
    }
  };

  /* ============== render ============== */
  const start = data?.start || {};
  const hasStart = !!(start.lat && start.lng) || !!start.address;

  return (
    <div className="space-y-4" data-testid="route-map-panel-v2">
      {/* Header / controls */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Field operations</div>
          <h2 className="text-2xl font-extrabold tracking-tighter font-[Outfit] text-gray-900">Route Map</h2>
          <p className="text-sm text-gray-600">Optimized driving order for the day — tap a pin for job actions.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setDate(shiftDate(date, -1))} data-testid="route-prev-day">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-white border border-gray-200">
            <CalendarIcon className="w-4 h-4 text-gray-500" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent text-sm font-bold outline-none"
              data-testid="route-date-picker"
            />
          </div>
          <Button variant="outline" size="sm" onClick={() => setDate(shiftDate(date, 1))} data-testid="route-next-day">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="route-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button
            size="sm"
            onClick={computeRoute}
            disabled={optimizing || !(orderedJobs.length || data?.jobs?.length)}
            data-testid="route-optimize-btn"
            className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold text-[11px] h-9 px-3"
          >
            {optimizing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
            Optimize Route
          </Button>
        </div>
      </div>

      {/* iter170 — Nearby Opportunities banner removed per business-owner
          request. To reintroduce, restore the block from git history at
          iter169. */}

      {/* Start location bar */}
      <div className="bg-white rounded-2xl border border-gray-200 p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="route-start-bar">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-emerald-50 text-[#047857] flex items-center justify-center shrink-0">
            <Crosshair className="w-4.5 h-4.5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Start location</div>
            <div className="text-sm font-extrabold text-gray-900 truncate">
              {hasStart ? (start.address || `${start.lat?.toFixed(4)}, ${start.lng?.toFixed(4)}`) : "Not set"}
            </div>
            <div className="text-[11px] text-gray-500">
              Mode: <span className="font-bold">{start.mode || "gps"}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={useCurrentGps} disabled={gpsBusy} data-testid="route-use-gps">
            {gpsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4 mr-1" />}
            Use current GPS
          </Button>
          <CustomStartInput onSave={saveCustomStart} initial={start.mode === "custom" ? start.address : ""} />
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="route-stats">
        <StatTile icon={MapPinned} label="Stops"      value={orderedJobs.length || (data?.jobs?.length || 0)} sub={unmappedCount ? `${unmappedCount} need address` : "all mapped"} />
        <StatTile icon={Route}     label="Miles"      value={fmtMiles(routeStats.meters)} sub={optimizing ? "optimizing…" : "optimized"} />
        <StatTile icon={Clock}     label="Drive time" value={fmtMinutes(routeStats.seconds)} sub="round trip excl. work" />
        <StatTile icon={DollarSign} label="Revenue"   value={`$${revenue.toFixed(0)}`} sub="all jobs on day" />
        <StatTile icon={Sparkles}  label="Density"    value={densityScore != null ? `${densityScore}` : "—"} sub="stops per mile²" />
      </div>

      {/* iter168 — Payment status filter chips. Default is "All" so
          every scheduled appointment is visible; the crew still needs to
          drive to unpaid / pay-after-service / overdue stops. Chips
          narrow just the stops list below, never the map pins. */}
      <div className="flex items-center gap-2 flex-wrap">
        <PaymentFilterStrip
          counts={data?.stats?.payment_status_counts || {}}
          totalJobs={(data?.jobs || []).length}
          selected={paymentFilter}
          onSelect={setPaymentFilter}
        />
        {/* iter169 — Show/hide completed jobs. Default hidden so the
            crew focuses on the day's remaining work. */}
        <button
          type="button"
          onClick={() => setShowCompleted((v) => !v)}
          data-testid="route-toggle-completed"
          data-state={showCompleted ? "on" : "off"}
          className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border transition ${
            showCompleted
              ? "bg-emerald-600 text-white border-emerald-700"
              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
          }`}
        >
          {showCompleted ? "Hiding: none" : "Show completed"}
        </button>
      </div>

      {/* Map + side list */}
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden relative" data-testid="route-map-canvas-wrapper">
          <div ref={mapElRef} className="w-full h-[460px] sm:h-[560px]" data-testid="route-map-canvas" />
          {/* iter170 — Empty state overlay when no active jobs remain.
              Sits over the map (which self-centers on Jax) so the crew
              sees a clear "you're done for the day" message instead of
              a blank map with no pins. */}
          {orderedJobs.length === 0 && !loading && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/90 backdrop-blur-sm"
              data-testid="route-complete-empty-state"
            >
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-[#047857] flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div className="text-lg font-extrabold tracking-tight font-[Outfit] text-gray-900">
                {showCompleted ? "No jobs for this day" : "Today's route is complete"}
              </div>
              <div className="text-sm text-gray-500 text-center max-w-xs">
                {showCompleted
                  ? "There are no scheduled or completed jobs on this date."
                  : "Every scheduled stop has been marked complete. Great work!"}
              </div>
            </div>
          )}
          {/* Export buttons */}
          {orderedJobs.length > 0 && (
            <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-white/95 backdrop-blur rounded-full border border-gray-200 px-1.5 py-1 shadow-sm">
              <a
                href={buildGoogleUrl()}
                target="_blank" rel="noopener noreferrer"
                data-testid="route-export-google"
                className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-emerald-50 text-[#047857] border border-emerald-200"
              >
                <ExternalLink className="w-3 h-3" /> Google
              </a>
              <a
                href={buildAppleUrl()}
                data-testid="route-export-apple"
                className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-gray-900 text-white border border-gray-900"
              >
                <ExternalLink className="w-3 h-3" /> Apple
              </a>
            </div>
          )}
          {loading && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[#047857]" />
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Optimized order</div>
              <div className="text-base font-extrabold tracking-tight font-[Outfit]">
                {paymentFilter === "all"
                  ? `${orderedJobs.length} stops`
                  : `${orderedJobs.filter((j) => matchesPaymentFilter(j, paymentFilter)).length} of ${orderedJobs.length} stops`}
              </div>
            </div>
            <LegendBadges />
          </div>
          <div className="divide-y divide-gray-100 overflow-y-auto max-h-[520px]" data-testid="route-stops-list">
            {orderedJobs.length === 0 && !loading && (
              <div className="px-4 py-12 text-center text-sm text-gray-500" data-testid="route-empty">
                No mapped jobs for this day yet.
              </div>
            )}
            {orderedJobs
              .filter((j) => matchesPaymentFilter(j, paymentFilter))
              .filter((j) => showCompleted || (j.status || "").toLowerCase() !== "completed")
              .map((j, idx) => {
                const isNextStop = idx === 0 && (j.status || "").toLowerCase() !== "completed"
                  && (j.status || "").toLowerCase() !== "cancelled"
                  && (j.status || "").toLowerCase() !== "canceled";
                return (
              <button
                key={j.booking_id}
                onClick={() => setSelectedJob(j)}
                className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3 relative ${
                  isNextStop ? "bg-emerald-50/60 ring-2 ring-inset ring-emerald-500" : ""
                }`}
                data-testid={`route-stop-${j.booking_id}`}
                data-next-stop={isNextStop ? "true" : "false"}
              >
                {isNextStop && (
                  <span
                    data-testid="route-next-stop-pill"
                    className="absolute top-1 right-2 text-[9px] font-black uppercase tracking-[0.16em] text-emerald-800 bg-emerald-200 border border-emerald-300 rounded-full px-1.5 py-0.5"
                  >Next</span>
                )}
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-extrabold shrink-0"
                  style={{ background: jobMarkerColor(j) }}
                >{j._stop}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">{j.preferred_time || ""}</div>
                    <StatusBadge status={j.status} />
                    <PaymentBadge paymentStatus={j.payment_status} booking={j} />
                    {/* iter171 — Extra Work indicator. Shows an amber pill
                        with the visit-specific add-on subtotal so the
                        crew knows extra work is expected on this stop.
                        Extras are always scoped to this visit only;
                        future recurring visits ignore them. */}
                    {(j.addons_subtotal || 0) > 0 && (
                      <span
                        data-testid={`route-stop-extras-${j.booking_id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300"
                      >
                        <Sparkles className="w-3 h-3" /> Extras +${Math.round(j.addons_subtotal || 0)}
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-extrabold text-gray-900 truncate">{j.service_name}</div>
                  <div className="text-xs text-gray-600 truncate">{j.name} · {j.phone || "—"}</div>
                  <div className="text-[11px] text-gray-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.formatted_address || j.address || "—"}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-extrabold tabular-nums">${(j.price || 0).toFixed(0)}</div>
                </div>
              </button>
                );
              })}
            {/* Opportunities (ghost rows) */}
            {showOpportunities && opportunities.length > 0 && (
              <div className="px-4 py-2 bg-amber-50/60 border-t border-amber-100">
                <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-amber-700 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Suggested · {opportunities.length}
                </div>
              </div>
            )}
            {showOpportunities && opportunities.map((o) => (
              <div
                key={`opp-${o.user_id}`}
                className="px-4 py-3 hover:bg-amber-50/50 flex items-start gap-3 bg-amber-50/20"
                data-testid={`route-opp-row-${o.user_id}`}
              >
                <button
                  onClick={() => setSelectedJob({ ...o, _is_opportunity: true, _stop: "+" })}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-amber-900 text-[12px] font-extrabold shrink-0 bg-amber-300 border-2 border-dashed border-amber-700"
                  title="View on map"
                >+</button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-[9px] font-extrabold uppercase text-amber-800">
                      {o.nearest_job?.distance_miles?.toFixed?.(1)} mi
                    </span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-indigo-50 border border-indigo-100 text-[9px] font-extrabold uppercase text-indigo-700">
                      {o.neighborhood}
                    </span>
                    <span className="text-[10px] font-extrabold text-emerald-700">+${o.savings.added_revenue.toFixed(0)} rev</span>
                    <span className="text-[10px] font-extrabold text-emerald-700">· ${o.savings.fuel_dollars.toFixed(2)} fuel</span>
                  </div>
                  <div className="text-sm font-extrabold text-gray-900 truncate">{o.name}</div>
                  <div className="text-[11px] text-gray-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" /> {o.address || "—"}</div>
                </div>
                <Button
                  size="sm"
                  onClick={(e) => handleAddOpportunity(o, e)}
                  disabled={!!oppBusy[o.user_id]}
                  className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-[10px] font-extrabold h-7 px-2.5 shrink-0"
                  data-testid={`route-add-opp-${o.user_id}`}
                >
                  {oppBusy[o.user_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Selected job sheet */}
      {selectedJob && (
        <JobSheet
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onUpdated={() => { loadDay(date); setSelectedJob(null); }}
        />
      )}
    </div>
  );
}

/* ============== sub-components ============== */

/**
 * iter168 — Client-side payment-status matcher shared by list filter,
 * "overdue" derivation, and analytics counts. Keeps derivation logic in
 * ONE place so the PaymentBadge in the list and the filter chip labels
 * always agree.
 */
function matchesPaymentFilter(job, filter) {
  if (!filter || filter === "all") return true;
  const s = (job.payment_status || "unpaid").toLowerCase();
  const jobStatus = (job.status || "").toLowerCase();
  const unpaidLike = ["unpaid", "payment_pending", "payment_failed", "pending_payment_verification"].includes(s);
  const derived = unpaidLike && jobStatus === "completed" ? "overdue" : s;
  if (filter === "paid") return derived === "paid" || derived === "paid_in_full";
  if (filter === "payment_pending") return derived === "pending_payment_verification" || (unpaidLike && jobStatus !== "completed");
  if (filter === "pay_on_arrival") return derived === "pay_on_arrival";
  if (filter === "overdue") return derived === "overdue" || derived === "payment_pending" || derived === "payment_failed";
  if (filter === "cancelled") return jobStatus === "cancelled" || jobStatus === "canceled";
  return derived === filter;
}

function PaymentFilterStrip({ counts, totalJobs, selected, onSelect }) {
  // iter168 — Chip label + how many jobs match each filter. Counts come
  // from the server (which computes per-status counts across the raw
  // bookings for the day). "Overdue" and "cancelled" are derived on the
  // client for the chip's own count.
  const paidC = (counts.paid || 0) + (counts.paid_in_full || 0);
  const pendingC = (counts.pending_payment_verification || 0)
                  + (counts.unpaid || 0)
                  + (counts.autopay_scheduled || 0);
  const paoC = counts.pay_on_arrival || 0;
  const overdueC = (counts.payment_pending || 0) + (counts.payment_failed || 0);
  const chips = [
    { key: "all",             label: "All",              count: totalJobs, classes: "bg-emerald-600 text-white border-emerald-700" },
    { key: "paid",            label: "Paid",             count: paidC,     classes: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    { key: "payment_pending", label: "Payment Pending",  count: pendingC,  classes: "bg-gray-100 text-gray-700 border-gray-200" },
    { key: "pay_on_arrival",  label: "Pay After Service", count: paoC,     classes: "bg-sky-100 text-sky-800 border-sky-200" },
    { key: "overdue",         label: "Overdue",          count: overdueC,  classes: "bg-orange-100 text-orange-900 border-orange-300" },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="route-payment-filter-strip">
      <span className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-gray-500 mr-1">Filter</span>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => onSelect(c.key)}
          data-testid={`route-payment-filter-${c.key}`}
          data-selected={selected === c.key ? "true" : "false"}
          className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border transition ${
            selected === c.key
              ? c.classes + " ring-2 ring-offset-1 ring-emerald-500"
              : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
          }`}
        >
          {c.label}
          <span className={`ml-0.5 tabular-nums font-black ${selected === c.key ? "" : "text-gray-500"}`}>
            {c.count}
          </span>
        </button>
      ))}
    </div>
  );
}


function StatTile({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 px-3.5 py-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="mt-0.5 text-xl font-extrabold tabular-nums tracking-tight font-[Outfit] text-gray-900">{value}</div>
      <div className="text-[10px] text-gray-500">{sub}</div>
    </div>
  );
}

function LegendBadges() {
  const items = [
    { c: "#059669", t: "Paid" },
    { c: "#EAB308", t: "Scheduled" },
    { c: "#F59E0B", t: "Active" },
    { c: "#1D4ED8", t: "Completed" },
    { c: "#DC2626", t: "Overdue" },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="route-legend">
      {items.map((i) => (
        <span key={i.t} className="inline-flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-[0.14em] text-gray-600">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: i.c }} />{i.t}
        </span>
      ))}
    </div>
  );
}

function CustomStartInput({ onSave, initial = "" }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(initial);
  useEffect(() => { if (initial) setVal(initial); }, [initial]);
  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="route-custom-start-open">
        <SettingsIcon className="w-4 h-4 mr-1" /> Custom address
      </Button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1.5">
      <Input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Crew start address"
        className="h-9 w-56"
        data-testid="route-custom-start-input"
      />
      <Button
        size="sm"
        onClick={() => { onSave(val); setOpen(false); }}
        disabled={!val || val.trim().length < 4}
        className="bg-[#047857] hover:bg-[#065F46] text-white"
        data-testid="route-custom-start-save"
      >
        Save
      </Button>
      <Button variant="ghost" size="sm" onClick={() => setOpen(false)}><X className="w-4 h-4" /></Button>
    </div>
  );
}

function JobSheet({ job, onClose, onUpdated }) {
  const tel = job.phone ? `tel:${String(job.phone).replace(/[^0-9+]/g, "")}` : null;
  const sms = job.phone ? `sms:${String(job.phone).replace(/[^0-9+]/g, "")}` : null;
  // iter169 — Prefer Apple Maps on iOS/iPadOS (turn-by-turn opens in
  // the native Apple Maps app). Fallback to Google Maps everywhere else.
  // The visible label follows the actual target so the crew knows which
  // app will open.
  const isIOS = typeof navigator !== "undefined" && (
    /iP(hone|od|ad)/.test(navigator.userAgent) ||
    // iPadOS 13+ masquerades as macOS but supports touch — detect that.
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );
  const destAddr = job.formatted_address || job.address;
  const directions = destAddr
    ? (isIOS
        ? `https://maps.apple.com/?daddr=${encodeURIComponent(destAddr)}&dirflg=d`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destAddr)}`)
    : null;
  const directionsAlt = destAddr && isIOS
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destAddr)}`
    : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="route-job-sheet">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span
                className="inline-flex items-center justify-center text-[11px] font-extrabold text-white rounded-full w-6 h-6"
                style={{ background: jobMarkerColor(job) }}
              >{job._stop}</span>
              <StatusBadge status={job.status} />
              <PaymentBadge paymentStatus={job.payment_status} booking={job} />
            </div>
            <div className="mt-1 text-lg font-extrabold tracking-tight font-[Outfit] truncate">{job.service_name}</div>
            <div className="text-sm text-gray-700 truncate">{job.name} · {job.phone || "—"}</div>
            <div className="text-[12px] text-gray-500 truncate flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.formatted_address || job.address}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="px-5 py-4 grid grid-cols-2 gap-2">
          {directions && (
            <a href={directions} target="_blank" rel="noopener noreferrer" data-testid="sheet-directions"
               className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-50 text-[#047857] border border-emerald-200 text-sm font-extrabold">
              <Navigation className="w-4 h-4" /> {isIOS ? "Apple Maps" : "Directions"}
            </a>
          )}
          {/* iter169 — On iOS, also expose Google Maps as a secondary tap so the
              crew can pick either app; on other platforms Google is already the
              primary "Directions" button so we don't render this secondary. */}
          {directionsAlt && (
            <a href={directionsAlt} target="_blank" rel="noopener noreferrer" data-testid="sheet-directions-google"
               className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-900 text-sm font-extrabold">
              <Navigation className="w-4 h-4" /> Google Maps
            </a>
          )}
          {tel && (
            <a href={tel} data-testid="sheet-call"
               className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gray-900 text-white text-sm font-extrabold">
              <Phone className="w-4 h-4" /> Call
            </a>
          )}
          {sms && (
            <a href={sms} data-testid="sheet-text"
               className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-gray-300 text-gray-900 text-sm font-extrabold">
              <MessageSquare className="w-4 h-4" /> Text
            </a>
          )}
          <a href={job.payment_status !== "paid" ? `/tip/${job.booking_id}` : "#"} data-testid="sheet-pay"
             className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 text-sm font-extrabold">
            <DollarSign className="w-4 h-4" /> Payment
          </a>
        </div>
        <div className="px-5 pb-4 flex items-center justify-between gap-2">
          <div className="text-xs text-gray-500">More actions:</div>
          <JobActionsMenu booking={job} onUpdated={onUpdated} />
        </div>

        {/* iter171 — Extra Work callout. Renders ONLY when this specific
            visit has add-ons attached; the crew sees exactly what
            additional work to perform on this stop. Extras are visit-
            scoped and never leak into future recurring visits. */}
        {(job.addons_subtotal || 0) > 0 && Array.isArray(job.additional_services) && job.additional_services.length > 0 && (
          <div className="mx-5 mb-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="jobsheet-extras-callout">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-extrabold text-amber-700">
              <Sparkles className="w-3.5 h-3.5" /> Extra Work for this visit only
            </div>
            <ul className="mt-2 space-y-1.5">
              {job.additional_services.map((a, i) => (
                <li key={a.extra_work_entry_id || i} className="flex items-start justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <div className="font-extrabold text-gray-900 truncate">
                      {a.label || a.service_name}
                      {a.quantity && a.quantity !== 1 ? <span className="text-gray-500"> × {a.quantity}</span> : null}
                    </div>
                    {a.description && (
                      <div className="text-xs text-gray-600 line-clamp-2">{a.description}</div>
                    )}
                    {a.internal_notes && (
                      <div className="text-[11px] text-amber-800 italic">Note: {a.internal_notes}</div>
                    )}
                  </div>
                  <div className="text-sm font-extrabold tabular-nums text-amber-900 shrink-0">+${Math.round(a.price || 0)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-amber-200 flex items-center justify-between text-xs">
              <span className="text-amber-800">Recurring price unchanged. Next visit returns to normal.</span>
              <span className="font-extrabold text-amber-900">Total extras: +${Math.round(job.addons_subtotal || 0)}</span>
            </div>
          </div>
        )}
        {/* iter135 — Per-Service Job Details (each service = independent Job) */}
        <div className="px-5 pb-3" data-testid="route-job-details-wrap">
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-900/60 mb-2">
            Job Details · Per Service
          </div>
          <JobDetailsList bookingId={job.booking_id} onUpdated={onUpdated} />
        </div>
        {/* iter132 — Legacy visit-wide crew workflow (On my way / Arrived / Started). */}
        <div className="px-5 pb-5" data-testid="crew-workflow-buttons-wrap">
          <CrewWorkflowButtons job={job} onUpdated={onUpdated} />
        </div>
      </div>
    </div>
  );
}
